
import {
    Component,
    effect,
    inject,
    input,
    model,
    output,
} from '@angular/core';
import { IUserAccountDto } from '@my-aon/auth';
import { TranslateService } from '@ngx-translate/core';
import {
    NotifyService,
    SharedModule,
} from '@my-aon/shared';
import { dxConfirm, validateGroup, DxFormItem } from '@my-aon/utils';
import { AdminAccountsService } from '../../../lib';
import { ResetPasswordRequest } from '../../../lib';

@Component({
    selector: 'lib-form-reset-password',
    templateUrl: './form-reset-password.component.html',
    styleUrl: './form-reset-password.component.scss',
    imports: [SharedModule],
})
export class FormResetPasswordComponent {
    readonly translateService = inject(TranslateService);
    readonly accountsService = inject(AdminAccountsService);
    readonly notify = inject(NotifyService);

    $submited = model<boolean>(false, { alias: 'submited' });
    onClose = output<IUserAccountDto | null>();

    $data = model.required<IUserAccountDto>({ alias: 'data' });

    readonly $validationGroupName = input<string>(
        'FormResetPasswordComponent' + 'ValidationGroupName',
        { alias: 'validationGroupName' },
    );

    formData: ResetPasswordRequest = { scheduledAtUtc: null };
    minDate: Date = new Date();
    loadIndicatorVisible: boolean = false;

    items: DxFormItem[] = [];

    constructor() {
        effect(() => {
            this.$data();
            this.resetForm();
        });

        effect(() => {
            this.$submited() && this.formSubmit();
        });

        this.items = [
            {
                dataField: 'scheduledAtUtc',
                editorType: 'dxDateBox',
                label: {
                    text: this.translateService.instant(
                        'ADMIN.ACCOUNTS.LABEL_RESET_PASSWORD_SCHEDULED_AT'
                    ),
                },
                editorOptions: {
                    type: 'datetime',
                    min: this.minDate,
                    showClearButton: true,
                    useMaskBehavior: true,
                },
            },
        ];
    }

    resetForm() {
        this.formData = { scheduledAtUtc: null };
        this.minDate = new Date();
    }

    validate = async () => {
        return await validateGroup(this.$validationGroupName());
    };

    formSubmit = async () => {
        if (this.loadIndicatorVisible) return;

        if (!(await this.validate())) {
            this.$submited.set(false);
            return;
        }

        const data = this.$data();
        const confirmed = await dxConfirm(
            this.translateService.instant(
                'ADMIN.ACCOUNTS.PROMPT_RESET_PASSWORD_BODY',
                {
                    name: this.escapeHtml(data.Name),
                    email: this.escapeHtml(data.Email),
                }
            ),
            this.translateService.instant(
                'ADMIN.ACCOUNTS.ACTION_RESET_PASSWORD'
            )
        );
        if (!confirmed) {
            this.$submited.set(false);
            return;
        }

        this.submit();
    };

    private submit = () => {
        const data = this.$data();
        const ACTION_LABEL = this.translateService.instant(
            'ADMIN.ACCOUNTS.ACTION_RESET_PASSWORD'
        );

        // Ensure ISO string or null
        const request: ResetPasswordRequest = {
            scheduledAtUtc: this.formData.scheduledAtUtc
                ? new Date(this.formData.scheduledAtUtc).toISOString()
                : null,
        };

        this.loadIndicatorVisible = true;
        this.accountsService.resetPassword(data, request).subscribe({
            next: () => {
                this.notify.success(
                    this.translateService.instant(
                        'ADMIN.ACCOUNTS.ACTION_RESET_PASSWORD_SUCCESS'
                    ),
                    ACTION_LABEL
                );
                this.loadIndicatorVisible = false;
                this.onClose.emit(data);
            },
            error: (err) => {
                this.notify.error(
                    this.translateService.instant(
                        'ADMIN.ACCOUNTS.ACTION_RESET_PASSWORD_ERROR',
                        { reason: err?.toString?.() ?? '' }
                    ),
                    ACTION_LABEL
                );
                this.loadIndicatorVisible = false;
                this.$submited.set(false);
            },
        });
    };

    private escapeHtml(value: string | null | undefined): string {
        if (!value) return '';
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
}
